import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
  Typography,
  IconButton,
  Chip,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import { apiService, Bundle, BundleField } from '../services/api';

interface CreateBundleModalProps {
  open: boolean;
  onClose: () => void;
  adminSecret: string;
  onBundleCreated: () => void;
  editBundle?: Bundle | null;
}

const CreateBundleModal: React.FC<CreateBundleModalProps> = ({
  open,
  onClose,
  adminSecret,
  onBundleCreated,
  editBundle
}) => {
  const isEditMode = !!editBundle;
  const [bundleName, setBundleName] = useState('');
  const [bundleVersion, setBundleVersion] = useState('');
  const [bundleClientName, setBundleClientName] = useState('');
  const [currentAdName, setCurrentAdName] = useState('');
  const [bundleAds, setBundleAds] = useState<string[]>([]);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState('');
  const [adInputError, setAdInputError] = useState('');
  
  // Bundle Fields state
  interface BundleFieldLocal {
    id?: number;
    tx_name: string;
    tx_client_field_name?: string;
    tx_description: string;
    ar_options: string[];
  }
  const [bundleFields, setBundleFields] = useState<BundleFieldLocal[]>([]);
  const [currentFieldName, setCurrentFieldName] = useState('');
  const [currentFieldClientName, setCurrentFieldClientName] = useState('');
  const [currentFieldDescription, setCurrentFieldDescription] = useState('');
  const [currentFieldOption, setCurrentFieldOption] = useState('');
  const [currentFieldOptions, setCurrentFieldOptions] = useState<string[]>([]);
  const [fieldInputError, setFieldInputError] = useState('');
  const [editingFieldIndex, setEditingFieldIndex] = useState<number | null>(null);
  const [isFetchingAds, setIsFetchingAds] = useState(false);

  // Effect to populate form when editing
  useEffect(() => {
    if (isEditMode && editBundle) {
      setBundleName(editBundle.tx_name);
      setBundleVersion(editBundle.tx_version);
      setBundleClientName(editBundle.tx_client_bundle_name || '');
      setBundleAds(editBundle.bundle_ads.map(ad => ad.tx_name));
      setBundleFields(editBundle.bundle_fields.map(field => ({
        id: field.id,
        tx_name: field.tx_name,
        tx_client_field_name: field.tx_client_field_name,
        tx_description: field.tx_description,
        ar_options: field.ar_options || []
      })));
    } else {
      setBundleName('');
      setBundleVersion('');
      setBundleClientName('');
      setBundleAds([]);
      setBundleFields([]);
    }
  }, [isEditMode, editBundle]);

  // Fetch available ads when bundle name and version are set
  useEffect(() => {
    const fetchAvailableAds = async () => {
      if (bundleName.trim() && bundleVersion.trim() && !isEditMode) {
        setIsFetchingAds(true);
        try {
          // Generate the URL to the ads.json file
          const baseUrl = 'https://iframe.extratime.cloud/extrastreamer-cloud';
          const adsJsonUrl = `${baseUrl}/${bundleName.trim()}/${bundleVersion.trim()}/ads.json`;
          console.log('Fetching ads from JSON file:', adsJsonUrl);

          // Try direct fetch with CORS headers
          const response = await fetch(adsJsonUrl, {
            method: 'GET',
            mode: 'cors',
            headers: {
              'Content-Type': 'application/json',
            },
          });

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const adsData = await response.json();
          console.log('Received ads data:', adsData);

          // Assuming the JSON is an array of objects with a 'name' key
          if (Array.isArray(adsData)) {
            const adNames = adsData
              .map(ad => ad && typeof ad === 'object' && typeof ad.name === 'string' && ad.name.trim())
              .filter(name => !!name); 
            console.log('Extracted ad names:', adNames);

            if (adNames.length > 0) {
              // Only add ads that aren't already in the list
              setBundleAds(prevAds => {
                const newAds = adNames.filter(name => !prevAds.includes(name));
                if (newAds.length > 0) {
                  console.log('Auto-populated ads:', newAds);
                  return [...prevAds, ...newAds];
                }
                return prevAds;
              });
            } else {
              console.log('No valid ad names found in JSON');
            }
          } else {
            console.log('Invalid JSON format - expected an array');
          }
        } catch (error) {
          console.error('Error fetching ads.json:', error);
          console.log('Direct fetch failed, you may need to configure CORS on the server or use a proxy');
        } finally {
          setIsFetchingAds(false);
        }
      }
    };

    // Debounce the fetch to avoid too many requests
    const timeoutId = setTimeout(fetchAvailableAds, 500);
    return () => clearTimeout(timeoutId);
  }, [bundleName, bundleVersion, isEditMode]);

  const handleAddAd = () => {
    const trimmedAdName = currentAdName.trim();

    if (!trimmedAdName) {
      setAdInputError('Ad name cannot be empty');
      return;
    }

    if (bundleAds.includes(trimmedAdName)) {
      setAdInputError('This ad name already exists in the list');
      return;
    }

    setBundleAds([...bundleAds, trimmedAdName]);
    setCurrentAdName('');
    setAdInputError('');
  };

  const handleRemoveAd = (adName: string) => {
    setBundleAds(bundleAds.filter(ad => ad !== adName));
  };

  const handleAdInputKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddAd();
    }
  };

  // Field management functions
  const handleAddFieldOption = () => {
    const trimmedOption = currentFieldOption.trim();
    
    if (!trimmedOption) {
      setFieldInputError('Field option cannot be empty');
      return;
    }
    
    if (currentFieldOptions.includes(trimmedOption)) {
      setFieldInputError('This option already exists in the list');
      return;
    }
    
    setCurrentFieldOptions([...currentFieldOptions, trimmedOption]);
    setCurrentFieldOption('');
    setFieldInputError('');
  };

  const handleRemoveFieldOption = (option: string) => {
    setCurrentFieldOptions(currentFieldOptions.filter(opt => opt !== option));
  };

  const handleFieldOptionKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddFieldOption();
    }
  };

  const handleSaveField = () => {
    const trimmedName = currentFieldName.trim();
    const trimmedDescription = currentFieldDescription.trim();
    
    if (!trimmedName) {
      setFieldInputError('Field name is required');
      return;
    }
    

    if (currentFieldOptions.length === 0) {
      setFieldInputError('At least one option is required');
      return;
    }

    if (editingFieldIndex !== null) {
      // Update existing field - preserve the ID
      const updatedFields = [...bundleFields];
      const existingField = updatedFields[editingFieldIndex];
      updatedFields[editingFieldIndex] = {
        ...existingField,
        tx_name: trimmedName,
        tx_client_field_name: currentFieldClientName.trim() || undefined,
        tx_description: trimmedDescription || '',
        ar_options: currentFieldOptions
      };
      setBundleFields(updatedFields);
      console.log('updatedFields', updatedFields)
      setEditingFieldIndex(null);
    } else {
      // Add new field
      const newField: BundleFieldLocal = {
        tx_name: trimmedName,
        tx_client_field_name: currentFieldClientName.trim() || undefined,
        tx_description: trimmedDescription || '',
        ar_options: currentFieldOptions
      };
      setBundleFields([...bundleFields, newField]);
    }

    // Reset form
    setCurrentFieldName('');
    setCurrentFieldClientName('');
    setCurrentFieldDescription('');
    setCurrentFieldOptions([]);
    setCurrentFieldOption('');
    setFieldInputError('');
  };

  const handleEditField = (index: number) => {
    const field = bundleFields[index];
    setCurrentFieldName(field.tx_name);
    setCurrentFieldClientName(field.tx_client_field_name || '');
    setCurrentFieldDescription(field.tx_description);
    setCurrentFieldOptions([...field.ar_options]);
    setEditingFieldIndex(index);
    setFieldInputError('');
  };

  const handleRemoveField = (index: number) => {
    setBundleFields(bundleFields.filter((_, i) => i !== index));
    if (editingFieldIndex === index) {
      setEditingFieldIndex(null);
      setCurrentFieldName('');
      setCurrentFieldClientName('');
      setCurrentFieldDescription('');
      setCurrentFieldOptions([]);
      setCurrentFieldOption('');
      setFieldInputError('');
    }
  };

  const handleCancelFieldEdit = () => {
    setEditingFieldIndex(null);
    setCurrentFieldName('');
    setCurrentFieldClientName('');
    setCurrentFieldDescription('');
    setCurrentFieldOptions([]);
    setCurrentFieldOption('');
    setFieldInputError('');
  };

  const handleCreate = async () => {
    if (!bundleName.trim()) {
      setError('Bundle name is required');
      return;
    }

    if (!bundleVersion.trim()) {
      setError('Bundle version is required');
      return;
    }

    setIsCreating(true);
    setError('');

    try {
      let bundle: Bundle;

      if (isEditMode && editBundle) {
        // Update existing bundle
        bundle = await apiService.updateBundle(adminSecret, editBundle.id, {
          tx_name: bundleName.trim(),
          tx_version: bundleVersion.trim(),
          tx_client_bundle_name: bundleClientName.trim() || undefined
        });

        // Handle bundle ads updates
        const currentAds = editBundle.bundle_ads;
        const currentAdNames = currentAds.map(ad => ad.tx_name);
        const newAdNames = bundleAds;

        // Find ads to remove, add, and potentially update
        const adsToRemove = currentAds.filter(ad => !newAdNames.includes(ad.tx_name));
        const adsToAdd = newAdNames.filter(name => !currentAdNames.includes(name));
        const adsToKeep = currentAds.filter(ad => newAdNames.includes(ad.tx_name));

        // Delete removed ads
        if (adsToRemove.length > 0) {
          await Promise.all(adsToRemove.map(ad =>
            apiService.deleteBundleAd(adminSecret, bundle.id, ad.id)
          ));
        }

        // Add new ads
        if (adsToAdd.length > 0) {
          await Promise.all(adsToAdd.map(adName =>
            apiService.createBundleAd(adminSecret, bundle.id, {
              tx_name: adName
            })
          ));
        }

        // Handle bundle fields updates
        const currentFields = editBundle.bundle_fields;
        const currentFieldNames = currentFields.map(f => f.tx_name);
        
        // Create a map of new fields by name for easy lookup
        const newFieldsMap = new Map(bundleFields.map(f => [f.tx_name, f]));
        
        // Find fields to remove, add, and update
        const fieldsToRemove = currentFields.filter(f => !newFieldsMap.has(f.tx_name));
        const fieldsToAdd = bundleFields.filter(f => !currentFieldNames.includes(f.tx_name));
        const fieldsToUpdate = currentFields.filter(f => newFieldsMap.has(f.tx_name));

        // Delete removed fields
        if (fieldsToRemove.length > 0) {
          await Promise.all(fieldsToRemove.map(field =>
            apiService.deleteBundleField(adminSecret, bundle.id, field.id)
          ));
        }

        // Update existing fields
        if (fieldsToUpdate.length > 0) {
          await Promise.all(fieldsToUpdate.map(field => {
            const newFieldData = newFieldsMap.get(field.tx_name)!;
            return apiService.updateBundleField(adminSecret, bundle.id, field.id, {
              tx_name: newFieldData.tx_name,
              tx_client_field_name: newFieldData.tx_client_field_name,
              tx_description: newFieldData.tx_description,
              ar_options: newFieldData.ar_options
            });
          }));
        }

        // Add new fields
        if (fieldsToAdd.length > 0) {
          await Promise.all(fieldsToAdd.map(field =>
            apiService.createBundleField(adminSecret, bundle.id, {
              tx_name: field.tx_name,
              tx_client_field_name: field.tx_client_field_name,
              tx_description: field.tx_description,
              ar_options: field.ar_options
            })
          ));
        }
      } else {
        // Create new bundle
        bundle = await apiService.createBundle(adminSecret, {
          tx_name: bundleName.trim(),
          tx_version: bundleVersion.trim(),
          tx_client_bundle_name: bundleClientName.trim() || undefined
        });

        // Then create all bundle_ads for this bundle
        if (bundleAds.length > 0) {
          const adPromises = bundleAds.map(adName =>
            apiService.createBundleAd(adminSecret, bundle.id, {
              tx_name: adName
            })
          );

          await Promise.all(adPromises);
        }

        // Create all bundle_fields for this bundle
        if (bundleFields.length > 0) {
          const fieldPromises = bundleFields.map(field =>
            apiService.createBundleField(adminSecret, bundle.id, {
              tx_name: field.tx_name,
              tx_client_field_name: field.tx_client_field_name,
              tx_description: field.tx_description,
              ar_options: field.ar_options
            })
          );

          await Promise.all(fieldPromises);
        }
      }

      setBundleName('');
      setBundleVersion('');
      setCurrentAdName('');
      setBundleAds([]);
      setBundleFields([]);
      setCurrentFieldName('');
      setCurrentFieldClientName('');
      setCurrentFieldDescription('');
      setCurrentFieldOptions([]);
      setCurrentFieldOption('');
      setEditingFieldIndex(null);
      onBundleCreated();
      onClose();
    } catch (error: any) {
      if (error.response?.data?.detail) {
        setError(error.response.data.detail);
      } else {
        setError(isEditMode ? 'Failed to update bundle' : 'Failed to create bundle');
      }
      console.error(isEditMode ? 'Error updating bundle:' : 'Error creating bundle:', error);
    } finally {
      setIsCreating(false);
    }
  };

  const handleClose = () => {
    if (!isCreating) {
      setBundleName('');
      setBundleVersion('');
      setCurrentAdName('');
      setBundleAds([]);
      setBundleFields([]);
      setCurrentFieldName('');
      setCurrentFieldClientName('');
      setCurrentFieldDescription('');
      setCurrentFieldOptions([]);
      setCurrentFieldOption('');
      setEditingFieldIndex(null);
      setError('');
      setAdInputError('');
      setFieldInputError('');
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEditMode ? 'Edit Bundle' : 'Create New Bundle'}</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {isEditMode
              ? 'Edit bundle information and manage associated advertisements.'
              : 'Create a new bundle to group advertisements and their configurations.'
            }
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <TextField
            autoFocus
            margin="dense"
            label="Bundle Name"
            fullWidth
            variant="outlined"
            value={bundleName}
            onChange={(e) => setBundleName(e.target.value)}
            disabled={isCreating}
            placeholder="e.g., sports-bundle"
            sx={{ mb: 2 }}
          />

          <TextField
            margin="dense"
            label="Bundle Version"
            fullWidth
            variant="outlined"
            value={bundleVersion}
            onChange={(e) => setBundleVersion(e.target.value)}
            disabled={isCreating}
            placeholder="e.g., 1.0.0"
            helperText="Version identifier for this bundle"
            sx={{ mb: 2 }}
          />

          <TextField
            margin="dense"
            label="Bundle Name for Client (Optional)"
            fullWidth
            variant="outlined"
            value={bundleClientName}
            onChange={(e) => setBundleClientName(e.target.value)}
            disabled={isCreating}
            placeholder="e.g., Sports Package"
            helperText="Custom name displayed to the client"
            sx={{ mb: 3 }}
          />

          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Bundle Ads {isFetchingAds && <CircularProgress size={16} sx={{ ml: 1 }} />}
          </Typography>

          {bundleAds.length > 0 && (
            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2 }}>
              {bundleAds.map((adName) => (
                <Chip
                  key={adName}
                  label={adName}
                  onDelete={() => handleRemoveAd(adName)}
                  deleteIcon={<DeleteIcon />}
                  variant="outlined"
                  sx={{
                    mb: 1,
                    borderColor: '#9ACD32',
                    color: '#9ACD32',
                    '& .MuiChip-deleteIcon': {
                      color: '#9ACD32',
                      '&:hover': {
                        color: '#7BA428'
                      }
                    }
                  }}
                />
              ))}
            </Stack>
          )}

          {adInputError && (
            <Alert severity="error" sx={{ mb: 1 }}>
              {adInputError}
            </Alert>
          )}

          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <TextField
              margin="dense"
              label="Ad Name"
              fullWidth
              variant="outlined"
              value={currentAdName}
              onChange={(e) => {
                setCurrentAdName(e.target.value);
                setAdInputError('');
              }}
              onKeyPress={handleAdInputKeyPress}
              disabled={isCreating}
              placeholder="Enter ad name and press Enter or click Add"
              helperText={`${bundleAds.length} ad(s) added${isFetchingAds ? ' - Fetching available ads...' : ''}`}
            />
            <IconButton
              onClick={handleAddAd}
              disabled={isCreating || !currentAdName.trim()}
              color="primary"
              sx={{ justifyContent: 'center', alignItems: 'center', width: '50px', height: '50px' }}
            >
              <AddIcon />
            </IconButton>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Bundle Fields Section */}
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Bundle Fields
          </Typography>

          {bundleFields.length > 0 && (
            <Box sx={{ mb: 2 }}>
              {bundleFields.map((field, index) => (
                <Accordion key={index} sx={{ mb: 1 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', mr: 2 }}>
                      <Box>
                        <Typography variant="subtitle2">{field.tx_name}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {field.tx_client_field_name ? `Client Field Name: ${field.tx_client_field_name} • ` : ''}
                          {field.tx_description ? `${field.tx_description} • ` : ''}
                          {field.ar_options.length} option(s)
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditField(index);
                          }}
                          color="primary"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveField(index);
                          }}
                          color="error"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography variant="body2" sx={{ mb: 1 }}>Options:</Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      {field.ar_options.map((option, optIndex) => (
                        <Chip
                          key={optIndex}
                          label={option}
                          size="small"
                          variant="outlined"
                          sx={{ mb: 1 }}
                        />
                      ))}
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          )}

          {/* Field Creation/Edit Form */}
          <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2, mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>
              {editingFieldIndex !== null ? 'Edit Field' : 'Add New Field'}
            </Typography>

            {fieldInputError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {fieldInputError}
              </Alert>
            )}

            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <TextField
                label="Field Name"
                fullWidth
                variant="outlined"
                value={currentFieldName}
                onChange={(e) => {
                  setCurrentFieldName(e.target.value);
                  setFieldInputError('');
                }}
                disabled={isCreating}
                placeholder="e.g., Category"
                size="small"
              />
              <TextField
                label="Client Field Name"
                fullWidth
                variant="outlined"
                value={currentFieldClientName}
                onChange={(e) => {
                  setCurrentFieldClientName(e.target.value);
                  setFieldInputError('');
                }}
                disabled={isCreating}
                placeholder="e.g., category_field"
                helperText="Name seen by client"
                size="small"
              />
            </Box>

            <Box sx={{ mb: 2 }}>
              <TextField
                label="Field Description (Optional)"
                fullWidth
                variant="outlined"
                value={currentFieldDescription}
                onChange={(e) => {
                  setCurrentFieldDescription(e.target.value);
                  setFieldInputError('');
                }}
                disabled={isCreating}
                placeholder="e.g., Select a category"
                size="small"
              />
            </Box>

            {/* Field Options */}
            <Typography variant="body2" sx={{ mb: 1 }}>
              Field Options ({currentFieldOptions.length})
            </Typography>

            {currentFieldOptions.length > 0 && (
              <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2 }}>
                {currentFieldOptions.map((option, index) => (
                  <Chip
                    key={index}
                    label={option}
                    onDelete={() => handleRemoveFieldOption(option)}
                    deleteIcon={<DeleteIcon />}
                    color="secondary"
                    variant="outlined"
                    size="small"
                    sx={{ mb: 1 }}
                  />
                ))}
              </Stack>
            )}

            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <TextField
                label="Option Value"
                fullWidth
                variant="outlined"
                value={currentFieldOption}
                onChange={(e) => {
                  setCurrentFieldOption(e.target.value);
                  setFieldInputError('');
                }}
                onKeyPress={handleFieldOptionKeyPress}
                disabled={isCreating}
                placeholder="Enter option value"
                size="small"
              />
              <IconButton
                onClick={handleAddFieldOption}
                disabled={isCreating || !currentFieldOption.trim()}
                color="primary"
                sx={{ justifyContent: 'center', alignItems: 'center', width: '50px', height: '40px' }}
              >
                <AddIcon />
              </IconButton>
            </Box>

            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              {editingFieldIndex !== null && (
                <Button
                  onClick={handleCancelFieldEdit}
                  disabled={isCreating}
                  startIcon={<CancelIcon />}
                  size="small"
                >
                  Cancel
                </Button>
              )}
              <Button
                onClick={handleSaveField}
                variant="contained"
                disabled={
                  isCreating || 
                  !currentFieldName.trim() || 
                  currentFieldOptions.length === 0
                }
                startIcon={<SaveIcon />}
                size="small"
              >
                {editingFieldIndex !== null ? 'Update Field' : 'Add Field'}
              </Button>
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isCreating}>
          Cancel
        </Button>
        <Button
          onClick={handleCreate}
          variant="contained"
          disabled={isCreating || !bundleName.trim() || !bundleVersion.trim()}
        >
          {isCreating ? (
            <>
              <CircularProgress size={20} sx={{ mr: 1 }} />
              {isEditMode ? 'Updating...' : 'Creating...'}
            </>
          ) : (
            isEditMode ? 'Update Bundle' : 'Create Bundle'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateBundleModal;